import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveImg(src) {
  if (!src) return "";
  if (src.startsWith("/uploads/")) return BACKEND_URL + src;
  return src;
}

function ItemRow({ item, onSave, onUpload, toast }) {
  const [es, setEs] = useState(item.es?.value || "");
  const [en, setEn] = useState(item.en?.value || "");
  const [esAlt, setEsAlt] = useState(item.es?.alt || "");
  const [enAlt, setEnAlt] = useState(item.en?.alt || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    setEs(item.es?.value || "");
    setEn(item.en?.value || "");
    setEsAlt(item.es?.alt || "");
    setEnAlt(item.en?.alt || "");
    setDirty(false);
  }, [item.cms_id]);

  const isImg = item.type === "img";

  const save = async () => {
    setSaving(true);
    try {
      await onSave(item.cms_id, {
        es: { value: es, alt: esAlt },
        en: { value: en, alt: enAlt },
      });
      setDirty(false);
      toast("Saved", "success");
    } catch (e) {
      toast("Save failed: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await onUpload(file);
      if (target === "es") setEs(url);
      else setEn(url);
      setDirty(true);
      toast("Image uploaded", "success");
    } catch (err) {
      toast("Upload failed", "error");
    }
  };

  return (
    <div className="item-row" data-testid={`item-${item.cms_id}`}>
      <div className="meta">
        <span className="id">{item.cms_id}</span>
        <span className={`type-badge ${isImg ? "img" : ""}`}>
          {isImg ? "Image" : "Text"}
        </span>
      </div>

      <div className="lang-cols">
        <div className="col">
          <label>Spanish (ES)</label>
          {isImg ? (
            <>
              <textarea
                value={es}
                onChange={(e) => { setEs(e.target.value); setDirty(true); }}
                placeholder="Image URL"
                rows={2}
                data-testid={`es-value-${item.cms_id}`}
              />
              {es && <div className="img-preview" style={{ backgroundImage: `url(${resolveImg(es)})` }} />}
              <input
                type="text"
                placeholder="Alt text"
                value={esAlt}
                onChange={(e) => { setEsAlt(e.target.value); setDirty(true); }}
                style={{ width: "100%", marginTop: 6, background: "#0f0f10", border: "1px solid #2a2a2e", color: "#fff", padding: 8, borderRadius: 6, boxSizing: "border-box" }}
              />
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInput}
                onChange={(e) => handleFileUpload(e, "es")}
              />
              <button
                type="button"
                className="btn secondary"
                style={{ marginTop: 6, fontSize: 12, padding: "6px 12px" }}
                onClick={() => fileInput.current?.click()}
              >
                Upload image
              </button>
            </>
          ) : (
            <textarea
              value={es}
              onChange={(e) => { setEs(e.target.value); setDirty(true); }}
              rows={Math.max(2, Math.min(8, (es.match(/\n/g) || []).length + 2))}
              data-testid={`es-value-${item.cms_id}`}
            />
          )}
        </div>

        <div className="col">
          <label>English (EN)</label>
          {isImg ? (
            <>
              <textarea
                value={en}
                onChange={(e) => { setEn(e.target.value); setDirty(true); }}
                placeholder="Image URL"
                rows={2}
                data-testid={`en-value-${item.cms_id}`}
              />
              {en && <div className="img-preview" style={{ backgroundImage: `url(${resolveImg(en)})` }} />}
              <input
                type="text"
                placeholder="Alt text"
                value={enAlt}
                onChange={(e) => { setEnAlt(e.target.value); setDirty(true); }}
                style={{ width: "100%", marginTop: 6, background: "#0f0f10", border: "1px solid #2a2a2e", color: "#fff", padding: 8, borderRadius: 6, boxSizing: "border-box" }}
              />
            </>
          ) : (
            <textarea
              value={en}
              onChange={(e) => { setEn(e.target.value); setDirty(true); }}
              rows={Math.max(2, Math.min(8, (en.match(/\n/g) || []).length + 2))}
              data-testid={`en-value-${item.cms_id}`}
            />
          )}
        </div>
      </div>

      <div className="actions">
        <button
          className="btn"
          onClick={save}
          disabled={saving || !dirty}
          data-testid={`save-${item.cms_id}`}
        >
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
      </div>
    </div>
  );
}

export default function AdminEditor() {
  const { page } = useParams();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (text, type = "info") => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 2200);
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/cms/items`, { params: { page }, withCredentials: true })
      .then((r) => {
        if (mounted) setItems(r.data.items || []);
      })
      .catch((e) => {
        if (e.response?.status === 401) navigate("/admin/login");
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [page, navigate]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (typeFilter !== "all" && it.type !== typeFilter) return false;
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        it.cms_id.toLowerCase().includes(q) ||
        (it.es?.value || "").toLowerCase().includes(q) ||
        (it.en?.value || "").toLowerCase().includes(q)
      );
    });
  }, [items, filter, typeFilter]);

  const handleSave = async (cms_id, body) => {
    const { data } = await axios.put(
      `${API}/cms/item/${cms_id}`,
      body,
      { withCredentials: true }
    );
    setItems((prev) => prev.map((i) => (i.cms_id === cms_id ? data : i)));
  };

  const handleUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/cms/upload`, fd, {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  return (
    <div className="admin-shell">
      <div className="admin-topbar">
        <h1>
          <Link to="/admin" style={{ color: "#fff", textDecoration: "none" }}>
            <span className="dot" /> Dosmass CMS
          </Link>
          <span style={{ fontWeight: 400, color: "#a0a0a8", marginLeft: 8, fontSize: 14 }}>
            / {page}
          </span>
        </h1>
        <div className="right">
          <a
            href={page === "home" ? "/" : `/${page === "about" ? "about" : page}`}
            target="_blank"
            rel="noreferrer"
            className="btn secondary"
            data-testid="preview-page"
          >
            Preview →
          </a>
          <span style={{ color: "#a0a0a8", fontSize: 13 }}>{user?.email}</span>
          <button
            className="btn danger"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            data-testid="logout-btn"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="editor-toolbar">
          <Link to="/admin" className="btn secondary" data-testid="back-to-pages">← Pages</Link>
          <input
            type="text"
            placeholder="Filter by ID or content…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="filter-input"
          />
          <div className="tab-group">
            <button
              className={typeFilter === "all" ? "active" : ""}
              onClick={() => setTypeFilter("all")}
              data-testid="filter-all"
            >All</button>
            <button
              className={typeFilter === "html" ? "active" : ""}
              onClick={() => setTypeFilter("html")}
              data-testid="filter-text"
            >Text</button>
            <button
              className={typeFilter === "img" ? "active" : ""}
              onClick={() => setTypeFilter("img")}
              data-testid="filter-img"
            >Images</button>
          </div>
          <span style={{ color: "#a0a0a8", fontSize: 13, marginLeft: "auto" }} data-testid="item-count">
            {filtered.length} / {items.length} items
          </span>
        </div>

        {loading ? (
          <p data-testid="loading-items">Loading items…</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: "#a0a0a8" }}>No items match your filter.</p>
        ) : (
          filtered.map((it) => (
            <ItemRow
              key={it.cms_id}
              item={it}
              onSave={handleSave}
              onUpload={handleUpload}
              toast={showToast}
            />
          ))
        )}
      </div>

      {toastMsg && (
        <div className={`toast ${toastMsg.type}`} data-testid="toast">
          {toastMsg.text}
        </div>
      )}
    </div>
  );
}
